import clsx from "clsx";
import { FC, ReactNode } from "react";

import { PageListItem } from "./PageListItem";

import { TAB_QUERY_PARAM } from "components/constants";
import { HoverableToolbar } from "components/hover-widgets/HoverableToolbar";
import { HideUnlessEditModeButton } from "components/materials/HideUnlessEditModeButton";
import { ActivityPageType, PageCategory, PageType } from "components/server-types";
import { Card } from "mds/components/Card";
import { DragAndDrop } from "mds/components/DragAndDrop";
import { useOrdered } from "mds/hooks/use-ordered";
import { store, useAppDispatch, useAppSelector } from "store/index";
import { selectCurrentPage, selectIsEditModeEnabled, selectTopicIdByPageId } from "store/selectors";
import { localStateActions } from "store/slices/local";
import { useObjectSelector } from "store/store-hooks";
import { useLocalCopy, usePageDragOrdering } from "utils/collections";
import { type PageListItemPage, usePageActions } from "utils/page";
import { CONTENT_SIDEBAR_TAB_NAME, pageUrl } from "utils/urls";

const PAGE_CATEGORY_TO_CONTENT_SIDEBAR_TAB_NAME: Record<PageCategory, CONTENT_SIDEBAR_TAB_NAME> = {
  activity_page: CONTENT_SIDEBAR_TAB_NAME.Content,
  assignment: CONTENT_SIDEBAR_TAB_NAME.Assignment,
  course_resource: CONTENT_SIDEBAR_TAB_NAME.CourseResource,
  instructor_workspace: CONTENT_SIDEBAR_TAB_NAME.InstructorWorkspace,
};

export type PagesCardProps = {
  pages: PageListItemPage[];
  header?: string | ReactNode;
  category?: PageCategory;
  activityId?: string;
  alwaysShowDueDates?: boolean;
  canManagePages?: boolean;
  shouldLinkSidebar?: boolean;
  addTopicParam?: boolean;
  showSectionTitle?: boolean;
  preserveOrder?: boolean;
};

// TODO: Consider consolidating with activity card
// This is s card component for displaying pages with a header element above.
// It's configurable in that pages can be draggable and allow for adding new
// pages.
export const PagesCard: FC<PagesCardProps> = ({
  header,
  pages,
  category = "activity_page",
  activityId,
  alwaysShowDueDates = false,
  showSectionTitle = false,
  canManagePages = false,
  shouldLinkSidebar,
  addTopicParam = false,
  preserveOrder = false,
}) => {
  const dispatch = useAppDispatch();
  const [_pages, setPages] = useLocalCopy(pages);
  const currentPage = useObjectSelector(selectCurrentPage, ["activity_id"]);
  const isEditModeEnabled = useAppSelector(selectIsEditModeEnabled);

  const defaultOrderedPages = useOrdered(_pages);
  const orderedPages = preserveOrder ? _pages : defaultOrderedPages;
  const isActivityScoped = !!activityId;
  // Section-page lists use PageCategory as dropContainerId — only rewrite activity_id when
  // this card is bound to a real activity.
  const movePageLocally = usePageDragOrdering(
    isActivityScoped ? activityId : undefined,
    pages,
    pages,
    setPages,
    isActivityScoped ? "activity_id" : undefined,
  );

  const isActive = isActivityScoped && currentPage?.activity_id === activityId;
  const { createPage, dropPage, navigateToPage } = usePageActions();

  const onPageClick = (page: Pick<PageType, "id" | "category" | "released_at" | "section_id">) => {
    const linkSidebar = shouldLinkSidebar
      ? PAGE_CATEGORY_TO_CONTENT_SIDEBAR_TAB_NAME[page.category]
      : undefined;
    const topicId = addTopicParam
      ? (selectTopicIdByPageId(store.getState(), page.id) ?? undefined)
      : undefined;
    navigateToPage(
      page,
      pageUrl(page.section_id, page.id, {
        [TAB_QUERY_PARAM]: linkSidebar,
        ...(topicId ? { topicId } : {}),
      }),
    );
    if (shouldLinkSidebar) {
      dispatch(localStateActions.setIsSidebarOpen(true));
    }
  };

  const createNewPage = () => {
    createPage(category, activityId);
  };

  const onDropPage = (droppedPage: ActivityPageType) => dropPage(droppedPage, { isActivityScoped });

  let headerElement = header ? (
    <div
      className={clsx("body-s mt-1 w-full truncate px-2 text-black-tint-40", {
        "cursor-pointer": isEditModeEnabled,
      })}
    >
      {header}
    </div>
  ) : null;

  if (canManagePages && header) {
    headerElement = (
      <DragAndDrop
        dragItemKind="page"
        dropContainerId={activityId || category}
        dropIndex={0}
        droppable={canManagePages && isEditModeEnabled}
        onDraggedItemHover={movePageLocally}
        onDropItem={onDropPage}
      >
        <HoverableToolbar reference="page" disableUnlessEditMode>
          <div
            className={clsx("body-s mt-1 w-full truncate px-2 text-black-tint-40", {
              "cursor-pointer": isEditModeEnabled,
            })}
          >
            {header}
          </div>
        </HoverableToolbar>
      </DragAndDrop>
    );
  }

  return (
    <>
      {headerElement}

      {orderedPages.length > 0 && (
        <Card className="mb-3 mt-2" isActive={isActive} small>
          {orderedPages?.map((page, index) => (
            <PageListItem
              alwaysShowDueDates={alwaysShowDueDates}
              containerId={activityId || category}
              key={page.id}
              page={page}
              pageIndex={index}
              showSectionTitle={showSectionTitle}
              onDropPage={canManagePages ? onDropPage : undefined}
              onMovePageLocally={canManagePages ? movePageLocally : undefined}
              onPageClick={() => onPageClick(page)}
            />
          ))}

          {canManagePages && (
            <div className="-ml-1">
              <HideUnlessEditModeButton reference="page" onClick={createNewPage} />
            </div>
          )}
        </Card>
      )}
    </>
  );
};
